<script setup lang="ts">
  import { computed, inject } from 'vue';

  import { TIMELINE_INJECTION } from '../Timeline/keys';

  const slots = defineSlots<{
    /**
     * The opposite content of the timeline item.
     */
    opposite?: () => Record<string, never>;

    /**
     * The default content of the timeline item.
     */
    default: () => Record<string, never>;
  }>();

  const timelineInjection = inject(TIMELINE_INJECTION.key);

  if (!timelineInjection) {
    throw Error('The TimelineItem component must be a child of the Timeline component.');
  }

  const { density, side } = timelineInjection;

  const sideClasses = computed(() => {
    return {
      main: {
        'col-start-3': side.value === 'end',
        'col-end-2': side.value === 'start',
      },
      opposite: {
        'col-end-2': side.value === 'end',
        'col-start-3': side.value === 'start',
      },
    };
  });

  const contentDensity = computed(() => ({
    'mb-3': density.value === 'compact',
    'mb-6': density.value === 'comfortable',
  }));
</script>

<template>
  <li class="stash-timeline-item group contents list-none" data-test="stash-timeline-item">
    <div
      class="stash-timeline-item__main-content"
      :class="[sideClasses.main, contentDensity]"
      data-test="stash-timeline-item|content"
    >
      <slot></slot>
    </div>

    <div
      class="stash-timeline-separator col-start-2 col-end-3 flex h-full flex-col items-center"
      data-test="stash-timeline-separator"
    >
      <div class="mt-[5px] rounded-full bg-ice-500 p-1.5"></div>
      <div class="w-[2px] flex-1 bg-ice-500"></div>
    </div>

    <div
      v-if="slots.opposite"
      class="stash-timeline-item__opposite-content"
      :class="[sideClasses.opposite, contentDensity]"
      data-test="stash-timeline-item|opposite-content"
    >
      <slot name="opposite"></slot>
    </div>
  </li>
</template>
